import { useMemo, useRef, useState } from 'react'
import { Select, Stack, Text, toast } from '@common/ui'
import { matchesMimeType } from '@common/plugin-host'
import AssetEditPage, { AssetPayloadConfig } from '../asset/AssetEditPage'
import { KindEditPageProps } from '../KindPages'
import {
  getEntityLinkFromMimeType,
  getEntityLinkFromResourceKey,
  getMimeTypeFromEntity,
  getResourceKeyFromEntity,
} from '../../../../io/entityResourceKeyLink'
import useUploadResource from '../../../../io/useUploadResource'
import { extensionForMimeType } from '../../../../util/mimeType'
import getResourceUrl from '../../../../io/api/resource/getResourceUrl'
import AssetPluginPreview from '../../../../plugins/AssetPluginPreview'
import useAssetPlugin, { creatableAssetKinds } from '../../../../plugins/useAssetPlugin'
import { resolveAssetEditor } from './assetEditors'
import AssetFilePayloadEditor from './AssetFilePayloadEditor'
import useObjectUrl from './useObjectUrl'
import useDebounced from '../../../../ui/debounce/useDebounced'

const UPLOAD_MODE = 'upload' as const

const centered = { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 } as const

const GenericAssetEditPage = (props: KindEditPageProps) => {
  const { entity } = props
  const { upload } = useUploadResource()

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)
  const [selectedMimeType, setSelectedMimeType] = useState<string | undefined>(undefined)
  const [textEdited, setTextEdited] = useState(false)
  const [authoringMimeType, setAuthoringMimeType] = useState<string | undefined>(undefined)
  const [liveText, setLiveText] = useState<string | undefined>(undefined)
  const [debouncedLiveText] = useDebounced(liveText, 300)

  const existingResourceKey = entity ? getResourceKeyFromEntity(entity) : undefined
  const existingMimeType = entity ? getMimeTypeFromEntity(entity) : undefined

  // A brand-new asset with neither link yet can be authored from a plugin's `creates` descriptor instead of uploaded.
  // Once either link exists the mime is immutable, so the picker never reappears.
  const showCreationPicker = !existingMimeType && !existingResourceKey
  const creatableKinds = useMemo(() => creatableAssetKinds(), [])
  const authoringKind = authoringMimeType
    ? creatableKinds.find((kind) => kind.creates.mimeType === authoringMimeType)
    : undefined
  const authoringCreates = authoringKind?.creates

  const kindDisabledForFile = (kind: (typeof creatableKinds)[number]): boolean =>
    !!selectedFile && !!selectedMimeType && !matchesMimeType(kind.mimeTypes, selectedMimeType)

  const previewMimeType = selectedMimeType ?? authoringCreates?.mimeType ?? existingMimeType
  const previewResourceKey = selectedFile ? undefined : existingResourceKey
  const selectedFileUrl = useObjectUrl(selectedFile)
  const plugin = useAssetPlugin(previewMimeType)

  const AssetEditor = authoringKind || existingMimeType ? resolveAssetEditor(previewMimeType) : undefined

  const resetEditingState = () => {
    setTextEdited(false)
    setLiveText(undefined)
  }

  const seedRequestRef = useRef(0)

  const seedEditor = (kind: (typeof creatableKinds)[number] | undefined, onChange: (value: string) => void) => {
    const request = ++seedRequestRef.current
    const resolveSeed = async () => {
      if (!kind) return ''
      if (selectedFile && selectedMimeType && matchesMimeType(kind.mimeTypes, selectedMimeType))
        return selectedFile.text()
      return ''
    }
    resolveSeed().then((seed) => {
      if (seedRequestRef.current !== request) return
      onChange(seed)
      setLiveText(seed)
    })
  }

  const liveTextBlob = useMemo(
    () =>
      AssetEditor && debouncedLiveText !== undefined && previewMimeType
        ? new Blob([debouncedLiveText], { type: previewMimeType })
        : undefined,
    [AssetEditor, debouncedLiveText, previewMimeType]
  )
  const liveTextUrl = useObjectUrl(liveTextBlob)

  const previewUrl = liveTextUrl
    ? liveTextUrl
    : selectedFile
      ? selectedFileUrl
      : previewResourceKey
        ? getResourceUrl(previewResourceKey)
        : undefined

  const config = useMemo<AssetPayloadConfig>(
    () => ({
      payloadLabel: 'Payload',
      payloadMimeType: previewMimeType,
      renderPayloadEditor: ({ value, onChange, editable }) => (
        <Stack gap="md">
          {showCreationPicker && (
            <Select
              size="sm"
              label="Asset kind"
              allowDeselect={false}
              style={{ alignSelf: 'flex-start', minWidth: 200 }}
              value={authoringMimeType ?? UPLOAD_MODE}
              data={[
                { value: UPLOAD_MODE, label: 'Upload a file' },
                ...creatableKinds.map((kind) => ({
                  value: kind.creates.mimeType,
                  label: kind.creates.label,
                  disabled: kindDisabledForFile(kind),
                })),
              ]}
              onChange={(next) => {
                const nextKind =
                  !next || next === UPLOAD_MODE ? undefined : creatableKinds.find((k) => k.creates.mimeType === next)
                setAuthoringMimeType(nextKind?.creates.mimeType)
                resetEditingState()
                seedEditor(nextKind, onChange)
              }}
            />
          )}
          {!authoringKind && (
            <AssetFilePayloadEditor
              existingResourceKey={existingResourceKey}
              onFileResolved={(file, mimeType) => {
                if (existingMimeType && mimeType !== existingMimeType) {
                  toast.negative(
                    `This asset is ${existingMimeType}; the selected file is ${mimeType}. Create a new asset instead.`
                  )
                  return
                }
                setSelectedFile(file)
                setSelectedMimeType(mimeType)
                resetEditingState()
              }}
            />
          )}
          {AssetEditor && (
            <AssetEditor
              value={value}
              editable={editable}
              onChange={(next: string) => {
                setTextEdited(true)
                setLiveText(next)
                onChange(next)
              }}
            />
          )}
        </Stack>
      ),
      renderPreview: () => {
        if (!previewUrl) {
          return (
            <div style={centered}>
              <Text c="dimmed">No file to preview</Text>
            </div>
          )
        }
        if (!plugin?.uiPluginId) return null
        return <AssetPluginPreview pluginId={plugin.uiPluginId} url={previewUrl} mimeType={previewMimeType} />
      },
      buildPayloadLinks: async ({ text }) => {
        if (authoringCreates) {
          try {
            const mimeType = selectedMimeType ?? authoringCreates.mimeType
            const extension = extensionForMimeType(mimeType, selectedFile?.name ?? '')
            const blob = new Blob([text], { type: mimeType })
            const resourceKey = await upload({ data: blob, extension })
            return [getEntityLinkFromResourceKey(resourceKey), getEntityLinkFromMimeType(mimeType)]
          } catch (e) {
            console.error(e)
            toast.negative('Failed to upload asset')
            throw e
          }
        }
        if (selectedFile && selectedMimeType) {
          try {
            const resourceKey = await upload({
              data: selectedFile,
              extension: extensionForMimeType(selectedMimeType, selectedFile.name),
            })
            return [getEntityLinkFromResourceKey(resourceKey), getEntityLinkFromMimeType(selectedMimeType)]
          } catch (e) {
            console.error(e)
            toast.negative('Failed to upload asset')
            throw e
          }
        }
        if (textEdited && existingMimeType) {
          try {
            const extension = extensionForMimeType(existingMimeType, '')
            const blob = new Blob([text], { type: existingMimeType })
            const resourceKey = await upload({ data: blob, extension })
            return [getEntityLinkFromResourceKey(resourceKey)]
          } catch (e) {
            console.error(e)
            toast.negative('Failed to upload asset')
            throw e
          }
        }
        return []
      },
    }),
    [
      existingResourceKey,
      existingMimeType,
      selectedFile,
      selectedMimeType,
      textEdited,
      showCreationPicker,
      creatableKinds,
      authoringKind,
      authoringCreates,
      AssetEditor,
      previewUrl,
      previewMimeType,
      plugin,
      upload,
    ]
  )

  return <AssetEditPage {...props} config={config} />
}

export default GenericAssetEditPage
